import { visibilityEpoch } from '../visibility-cache'
import type { DashboardUser } from '../../features/auth/auth-adapter'
import { ApiError } from './client'

export async function fetchAuthenticatedAudio({
  baseUrl,
  path,
  getUser,
  fetchImplementation = fetch,
  signal,
}: {
  baseUrl: string
  path: string
  getUser: () => DashboardUser | null
  fetchImplementation?: typeof fetch
  signal?: AbortSignal
}) {
  const epoch = visibilityEpoch()
  const user = getUser()
  if (!user) {
    throw new ApiError('unauthenticated', 'Authentication is required')
  }
  let token: string
  try {
    token = await user.getIdToken()
  } catch {
    throw new ApiError(
      'unauthenticated',
      'Authentication could not be refreshed'
    )
  }
  if (!token) {
    throw new ApiError(
      'unauthenticated',
      'Authentication could not be refreshed'
    )
  }
  const headers = new Headers({
    Accept: 'audio/*',
    Authorization: `Bearer ${token}`,
  })
  let response: Response
  try {
    response = await fetchImplementation(
      `${baseUrl.replace(/\/$/, '')}/${path.replace(/^\//, '')}`,
      { method: 'GET', headers, signal }
    )
  } catch (error) {
    if (signal?.aborted) throw error
    throw new ApiError('transport', 'The recording service is unavailable')
  }
  if (!response.ok) {
    throw new ApiError(
      'server',
      response.status === 404
        ? 'Recording unavailable'
        : 'The recording could not be loaded',
      response.status
    )
  }
  const contentType = response.headers.get('Content-Type') ?? ''
  if (!contentType.toLowerCase().startsWith('audio/')) {
    throw new ApiError(
      'invalid-response',
      'The recording service returned an invalid response'
    )
  }
  const blob = await response.blob()
  if (blob.size === 0) {
    throw new ApiError(
      'invalid-response',
      'The recording service returned an invalid response'
    )
  }
  if (epoch !== visibilityEpoch() || user.uid !== getUser()?.uid) {
    throw new ApiError('unauthenticated', 'Dashboard visibility changed')
  }
  return blob
}
